import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextareaAutosize,
	IconButton,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import CollapseSection from "../../../components/ui/CollapseSection";
import CloseIcon from "@mui/icons-material/Close";
import {
	approvalQuotaWeekDisplayValue,
	deriveApprovalQuotaDetailsLayout,
	getApprovalQuotaBreakdownPeriodHint,
	scenarioApprovalPeriodTypeIsWeekly,
} from "./quotaUtils";

const DisplayComments = (params) => {
	const [open, setOpen] = useState(false);

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

	const handleClose = () => {
		setOpen(false);
	};

	return (
		<>
			<IconButton onClick={() => setOpen(true)} title={params.comments}>
				{params?.comments ? (
					<ForumOutlinedIcon
						title={params.comments}
						sx={{ color: "darkred.light" }}
					/>
				) : (
					<ForumOutlinedIcon />
				)}
			</IconButton>

			<Dialog
				disableEnforceFocus
				fullScreen={fullScreen}
				open={open}
				onClose={handleClose}
				aria-labelledby="responsive-dialog-title"
			>
				<DialogTitle id="responsive-dialog-title">Comments</DialogTitle>
				<IconButton
					onClick={handleClose}
					sx={{
						position: "absolute",
						top: 10,
						right: 10,
						zIndex: 1,
						color: "grey",
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<DialogContentText>
						<TextareaAutosize
							aria-label="minimum height"
							minRows={5}
							readOnly
							placeholder=""
							style={{ width: 500, fontSize: 18 }}
							defaultValue={params.comments}
						/>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						variant="outlined"
						onClick={handleClose}
						size="small"
						sx={{
							bgcolor: "darkred.light",
							color: "white.main",
							textTransform: "none",
							fontWeight: "bold",
							"&:hover": {
								bgcolor: "darkred.main",
								color: "white.main",
							},
						}}
					>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const numFmt = (params) => params.value?.toLocaleString("en-US");

/** Baseline | all-biweekly (First/Second Half) | weekly (Week1–4/5 + optional masking). */
function getApprovalQuotaDetailColumns(
	layout,
	showWeek5,
	quotaPeriodModel,
	applyWeekCellMasking,
	omitDepletionTypeColumn
) {
	const weekFmt =
		layout === "weekly"
			? (weekIdx) => (params) => {
					const row = params.row ?? params.api?.getRow?.(params.id) ?? {};
					return approvalQuotaWeekDisplayValue(
						row,
						weekIdx,
						quotaPeriodModel,
						Boolean(applyWeekCellMasking)
					);
			  }
			: () => null;
	const columns = [
		{
			field: "customerName",
			headerName: "Customer / CG",
			flex: 1,
			minWidth: 150,
		},
		{
			field: "customerNumber",
			headerName: "Customer Number",
			flex: 1,
			minWidth: 100,
		},
		{ field: "skuName", headerName: "SKU", flex: 1, minWidth: 150 },
		{
			field: "skuNumb",
			headerName: "SKU Number",
			flex: 1,
			minWidth: 100,
		},
	];
	if (!omitDepletionTypeColumn) {
		columns.push({
			field: "depletionModelName",
			headerName: "Depletion Type",
			flex: 1,
			minWidth: 100,
		});
	}
	columns.push(
		{
			field: "initialQuota",
			headerName: "Initial Quota",
			flex: 1,
			minWidth: 100,
			valueFormatter: numFmt,
		},
		{
			field: "safetyMargin",
			headerName: "Safety Margin",
			flex: 1,
			minWidth: 100,
			valueFormatter: numFmt,
		},
		{
			field: "tender",
			headerName: "Tender",
			flex: 1,
			minWidth: 100,
			valueFormatter: numFmt,
		},
		{
			field: "adjustment",
			headerName: "Adjustment",
			flex: 1,
			minWidth: 100,
			valueFormatter: numFmt,
		},
		{
			field: "totalQuota",
			headerName: "Total Quota",
			flex: 1,
			minWidth: 100,
			valueFormatter: numFmt,
		}
	);

	if (layout === "biweekly") {
		columns.push(
			{
				field: "weekly1",
				headerName: "First Half",
				flex: 1,
				minWidth: 100,
				valueFormatter: numFmt,
			},
			{
				field: "weekly2",
				headerName: "Second Half",
				flex: 1,
				minWidth: 100,
				valueFormatter: numFmt,
			}
		);
	} else if (layout === "weekly") {
		columns.push(
			{
				field: "weekly1",
				headerName: "Week 1",
				flex: 1,
				minWidth: 100,
				valueFormatter: weekFmt(1),
			},
			{
				field: "weekly2",
				headerName: "Week 2",
				flex: 1,
				minWidth: 100,
				valueFormatter: weekFmt(2),
			},
			{
				field: "weekly3",
				headerName: "Week 3",
				flex: 1,
				minWidth: 100,
				valueFormatter: weekFmt(3),
			},
			{
				field: "weekly4",
				headerName: "Week 4",
				flex: 1,
				minWidth: 100,
				valueFormatter: weekFmt(4),
			}
		);
		if (showWeek5) {
			columns.push({
				field: "weekly5",
				headerName: "Week 5",
				flex: 1,
				minWidth: 100,
				valueFormatter: weekFmt(5),
			});
		}
	}

	columns.push({
		field: "customerQuotaComments",
		headerName: "Customer Quota Comments",
		flex: 1,
		minWidth: 100,
		align: "center",
		headerAlign: "center",
		renderCell: (cellValues) => {
			return <DisplayComments comments={cellValues.value} />;
		},
	});

	return columns;
}

function resolveSelectedApprovalRow(state) {
	const id = state.selectedQuotaid;
	if (id == null) return null;
	const fromPayload = state.quotaForApproval?.find((q) => q.quotaId === id);
	if (fromPayload) return fromPayload;
	return (
		state.quotaForApprovalTable?.rows?.find((r) => r.quotaId === id) ?? null
	);
}

export default function QuotaDetailsTable() {
	const state = useSelector((store) => store.quota);
	const { quotaDetailsTable, selectedQuotaid, quotaCustomerBreakdown } = state;

	const [paginationModel, setPaginationModel] = useState({
		pageSize: 20,
		page: 0,
	});

	if (!quotaDetailsTable?.rows?.length || !quotaCustomerBreakdown) {
		return null;
	}

	const approvalRow = resolveSelectedApprovalRow(state);
	const periodHint = getApprovalQuotaBreakdownPeriodHint(approvalRow);
	const quotaPeriodModel =
		quotaCustomerBreakdown.periodDepletionModel ??
		quotaCustomerBreakdown.PeriodDepletionModel ??
		"";

	const layoutSpec = deriveApprovalQuotaDetailsLayout(
		quotaDetailsTable.rows,
		periodHint,
		quotaPeriodModel
	);
	const columns = getApprovalQuotaDetailColumns(
		layoutSpec.layout,
		layoutSpec.showWeek5,
		quotaPeriodModel,
		layoutSpec.applyWeekCellMasking,
		scenarioApprovalPeriodTypeIsWeekly(approvalRow)
	);
	const { rows } = quotaDetailsTable;

	return (
		<Box sx={{ mt: 5, maxWidth: "100%" }}>
			<CollapseSection title="Quota Details">
				<DataGrid
					style={{ maxHeight: "calc(100vh - 130px)" }}
					sx={{ marginTop: 2 }}
					rows={rows}
					columns={columns}
					disableColumnMenu
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					pageSizeOptions={[20, 50, 100]}
					headerHeight={40}
				/>
			</CollapseSection>
		</Box>
	);
}
